import { ProcessingOrder } from "../types";
import { getFixedAuthToken, getAuthorizationHeader } from "./authConfig";

// Базовый URL через Vite proxy (/1c-api -> https://clode.ru/38907428)
const API_BASE = "/1c-api/odata/standard.odata/";

/** Заголовок Authorization для запросов к 1С */
const get1CAuthHeader = (): string | null => {
  const t = getFixedAuthToken();
  return t ? getAuthorizationHeader(t) : null;
};

const fetchWithRetry = async (url: string, options: RequestInit, retries = 2): Promise<Response> => {
  try {
    const res = await fetch(url, options);
    if (!res.ok && res.status >= 500) throw new Error("Server error");
    return res;
  } catch (err: any) {
    if (retries > 0 && err.name !== 'AbortError') {
      await new Promise(r => setTimeout(r, 1000));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw err;
  }
};

/** Извлекает Ref_Key из значения (строка GUID или объект { Ref_Key } в 1С OData). */
const getRefKey = (ref: unknown): string | null => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref.trim() || null;
  if (typeof ref === 'object' && ref !== null && 'Ref_Key' in ref) {
    const key = (ref as { Ref_Key?: string }).Ref_Key;
    return typeof key === 'string' ? key.trim() || null : null;
  }
  return null;
};

const BATCH_SIZE = 15;

/**
 * Загрузка наименований из справочника по списку ключей (батчами по BATCH_SIZE).
 */
const fetchDescriptionsByKeys = async (
  resource: string,
  keys: string[],
  authHeader: string,
  signal?: AbortSignal
): Promise<Record<string, string>> => {
  const unique = [...new Set(keys)].filter(Boolean);
  if (unique.length === 0) return {};
  const result: Record<string, string> = {};
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const batch = unique.slice(i, i + BATCH_SIZE);
    const filter = batch.map(k => `Ref_Key eq guid'${k}'`).join(' or ');
    const url = `${API_BASE}${resource}?$select=Ref_Key,Description&$filter=${encodeURIComponent(filter)}&$format=json`;
    try {
      const res = await fetchWithRetry(url, { headers: { 'Authorization': authHeader, 'Accept': 'application/json' }, signal });
      const data = await res.json();
      (data.value || []).forEach((item: any) => {
        const key = getRefKey(item.Ref_Key) ?? item.Ref_Key;
        if (key) result[key] = (item.Description ?? '').trim() || key;
      });
    } catch (e: any) {
      if (e.name !== 'AbortError') console.warn(`ProcessingAPI: Failed to fetch batch ${Math.floor(i / BATCH_SIZE) + 1} of ${resource}`, e.message);
    }
  }
  return result;
};

/**
 * Загрузка договоров из Catalog_ДоговорыКонтрагентов (Description для колонки Contract) — батчами по BATCH_SIZE.
 */
const fetchContractDescriptions = async (
  keys: string[],
  authHeader: string,
  signal?: AbortSignal
): Promise<Record<string, string>> => {
  const unique = [...new Set(keys)].filter(Boolean);
  if (unique.length === 0) return {};
  const result: Record<string, string> = {};
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const batch = unique.slice(i, i + BATCH_SIZE);
    const filter = batch.map(k => `Ref_Key eq guid'${k}'`).join(' or ');
    const url = `${API_BASE}Catalog_ДоговорыКонтрагентов?$select=Ref_Key,Description,НомерДоговора&$filter=${encodeURIComponent(filter)}&$format=json`;
    try {
      const res = await fetchWithRetry(url, { headers: { 'Authorization': authHeader, 'Accept': 'application/json' }, signal });
      const data = await res.json();
      (data.value || []).forEach((item: any) => {
        const key = getRefKey(item.Ref_Key) ?? item.Ref_Key;
        if (key) {
          const display = (item.Description ?? item.НомерДоговора ?? '').trim();
          result[key] = display || '';
        }
      });
    } catch (e: any) {
      if (e.name !== 'AbortError') console.warn(`ProcessingAPI: Failed to fetch contracts batch ${Math.floor(i / BATCH_SIZE) + 1}`, e.message);
    }
  }
  return result;
};

export const getProcessingOrders = async (
  orgId?: string,
  suppId?: string,
  signal?: AbortSignal,
  commentSearch?: string
): Promise<ProcessingOrder[]> => {
  const authHeader = get1CAuthHeader();
  if (!authHeader) return [];

  const select = "$select=Ref_Key,Date,Number,Организация_Key,Контрагент_Key,Договор_Key,СуммаДокумента,Комментарий";
  let filter = "DeletionMark eq false and ВидОперации eq 'ЗаказНаПереработку'";
  
  if (orgId) filter += ` and Организация_Key eq guid'${orgId}'`;
  if (suppId) filter += ` and Контрагент_Key eq guid'${suppId}'`;
  const normalizedComment = commentSearch?.trim();
  if (normalizedComment) {
    const escapedComment = normalizedComment.replace(/'/g, "''");
    filter += ` and substringof('${escapedComment}',Комментарий) eq true`;
  }

  const initialUrl = `${API_BASE}Document_ЗаказПоставщику?${select}&$filter=${encodeURIComponent(filter).replace(/'/g, "%27")}&$format=json&$top=40&$orderby=Date desc`;

  try {
    const raw: any[] = [];
    const requestedUrls = new Set<string>();
    let url: string | undefined = initialUrl;

    while (url && !requestedUrls.has(url)) {
      requestedUrls.add(url);
      const response = await fetchWithRetry(url, { headers: { 'Authorization': authHeader, 'Accept': 'application/json' }, signal });
      if (!response.ok) throw new Error(`Failed to fetch processing orders: ${response.status}`);
      const data = await response.json();
      raw.push(...(data.value || []));

      // При поиске по комментарию обходим все страницы совпадений; без него сохраняем прежний лимит в 40 заказов.
      const nextLink = normalizedComment
        ? data['odata.nextLink'] ?? data['@odata.nextLink']
        : undefined;
      if (typeof nextLink === 'string' && nextLink) {
        url = new URL(nextLink, response.url || `${window.location.origin}${url}`).toString();
      } else {
        url = undefined;
      }
    }

    // Собираем уникальные ключи организаций, поставщиков и договоров (нормализуем ссылки: в 1С OData они могут быть объектом { Ref_Key } или строкой)
    const orgKeys = raw.map((o: any) => getRefKey(o.Организация_Key) ?? o.Организация_Key).filter(Boolean);
    const suppKeys = raw.map((o: any) => getRefKey(o.Контрагент_Key) ?? o.Контрагент_Key).filter(Boolean);
    const contractKeys = raw.map((o: any) => getRefKey(o.Договор_Key) ?? o.Договор_Key).filter(Boolean);

    // Параллельно загружаем наименования и договоры (все запросы — батчами по 15 ключей)
    const [orgMap, suppMap, contractMap] = await Promise.all([
      fetchDescriptionsByKeys("Catalog_Организации", orgKeys, authHeader, signal),
      fetchDescriptionsByKeys("Catalog_Контрагенты", suppKeys, authHeader, signal),
      fetchContractDescriptions(contractKeys, authHeader, signal)
    ]);

    // Добавляем наименования и номер договора в каждый заказ
    return raw.map((o: any) => {
      const orgKey = getRefKey(o.Организация_Key) ?? o.Организация_Key;
      const suppKey = getRefKey(o.Контрагент_Key) ?? o.Контрагент_Key;
      const contractKey = getRefKey(o.Договор_Key) ?? o.Договор_Key;
      const contractNumber = contractKey && contractMap[contractKey] ? contractMap[contractKey] : '-';
      return {
        ...o,
        Organization_Description: orgMap[orgKey] ?? o.Организация_Key,
        Supplier_Description: suppMap[suppKey] ?? o.Контрагент_Key,
        Contract_Number: contractNumber
      };
    });
  } catch (e: any) {
    if (e.name === 'AbortError') throw e;
    if (e.name !== 'AbortError') {
        console.error("ProcessingAPI: Error fetching orders", e.message);
    }
    return [];
  }
};
